<template>
  <div class="counterparty-details-section">
    <div class="counterparty-details-section__prompt">
      {{ $t("production_strategy.what_is_this_ppa_for") }}
    </div>

    <!-- Transaction Type Selection -->
    <SegmentedControl
      :modelValue="actionInstructions.transaction_type"
      :options="transactionTypeOptions"
      variant="primary"
      :aria-label="$t('production_strategy.transaction_type_aria')"
      @update:modelValue="updateTransactionType"
    />

    <InfoCallout
      :icon="
        actionInstructions.transaction_type === 'SEND' ? 'paper-plane' : 'ban'
      "
      class="counterparty-details-section__info"
    >
      <span v-if="actionInstructions.transaction_type === 'SEND'">
        <strong>{{ $t("production_strategy.send_label") }}</strong>
        {{ $t("production_strategy.send_explainer") }}
      </span>
      <span v-else>
        <strong>{{ $t("production_strategy.cancel_on_behalf_label") }}</strong>
        {{ $t("production_strategy.cancel_on_behalf_explainer") }}
      </span>
    </InfoCallout>

    <!-- Validation Error for Transaction Type -->
    <div v-if="v$.transaction_type.$error" class="text-danger small mb-3">
      <FontAwesomeIcon icon="exclamation-triangle" class="me-1" />
      <span v-for="error of v$.transaction_type.$errors" :key="error.$uid">
        {{ error.$message }}
      </span>
    </div>

    <!-- Transaction Details -->
    <div>
      <!-- Send Template -->
      <div v-if="actionInstructions.transaction_type === 'SEND'">
        <CnmcDestinationEditor
          v-if="usesCnmcIssuance"
          v-model="cnmcRecipient"
          :destinations="cnmcDestinations"
          canSwitchRoute
        />
        <SendRecipientForm
          v-else
          ref="sendRecipientForm"
          v-model="sendRecipient"
          :authority="null"
        />
      </div>

      <!-- Cancel Statement -->
      <div v-if="actionInstructions.transaction_type === 'CANCEL'">
        <CancelGoStatementComponent
          v-if="actionInstructions.cancel_statement"
          :statement="actionInstructions.cancel_statement"
          :statementIndex="0"
          :omitVolumePeriodRegistry="true"
          :showPrivacyToggle="true"
          @update:statement="updateCancelStatement"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoCallout from "@/components/common/InfoCallout.vue";
import SegmentedControl, {
  type SegmentedControlOption,
} from "@/components/common/SegmentedControl.vue";
import { useValidators } from "@/composables/useValidators";
import CancelGoStatementComponent from "@/dashboard/inventory_management/company_orders/CancelGoStatementComponent.vue";
import type { SendRecipient } from "@/dashboard/inventory_management/send/sendRecipient";
import SendRecipientForm from "@/dashboard/inventory_management/send/SendRecipientForm.vue";
import {
  createEmptySendTemplate,
  initializeTransactionTemplates,
  toSendRecipient,
  toSendTemplate,
} from "@/dashboard/production_strategy/overview/PPA/ppaActionUtils";
import type { ActionInstructions, IndividualCancelStatement } from "@/types";
import { PpaActionTransactionType } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useVuelidate } from "@vuelidate/core";
import { computed, useTemplateRef } from "vue";
import CnmcDestinationEditor from "@/dashboard/production_strategy/issuance-reporting/es/CnmcDestinationEditor.vue";
import {
  recipientDraftOf,
  toRecipient,
} from "@/dashboard/production_strategy/issuance-reporting/es/cnmcRecipientRules";
import type { CnmcRecipientDraft } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcTypes";
import { useI18n } from "vue-i18n";

withDefaults(
  defineProps<{
    usesCnmcIssuance?: boolean;
    cnmcDestinations?: { region: string; name: string }[];
  }>(),
  { usesCnmcIssuance: false, cnmcDestinations: () => [] }
);

const actionInstructions = defineModel<ActionInstructions>({ required: true });

const sendRecipientForm = useTemplateRef("sendRecipientForm");

// The template stores the recipient's region, which names its route: Spain (also an empty template's
// default) is a domestic transfer, anything else an export.
const cnmcRecipient = computed<CnmcRecipientDraft>({
  get: () => {
    const template = actionInstructions.value.send_template;
    return recipientDraftOf({
      name: template?.recipient_company_name || "",
      region: template?.recipient_registry_country ?? "ES",
      eecs: template?.recipient_eecs_account_number,
      r2: template?.recipient_cnmc_r2,
    });
  },
  set: (draft) => {
    const recipient = toRecipient(draft);
    actionInstructions.value = {
      ...actionInstructions.value,
      send_template: {
        ...createEmptySendTemplate(),
        recipient_company_name: recipient.name,
        recipient_registry_country: recipient.region,
        recipient_eecs_account_number: recipient.eecs || null,
        recipient_cnmc_r2: recipient.r2 ?? null,
      },
    };
  },
});
const { t } = useI18n();
const customValidators = useValidators(t);

const transactionTypeOptions = computed(
  (): SegmentedControlOption<PpaActionTransactionType>[] => [
    {
      value: PpaActionTransactionType.SEND,
      label: t("production_strategy.send_certificates"),
      icon: "paper-plane",
    },
    {
      value: PpaActionTransactionType.CANCEL,
      label: t("production_strategy.cancel_on_behalf"),
      icon: "ban",
    },
  ]
);

const sendRecipient = computed<SendRecipient>({
  get: () => toSendRecipient(actionInstructions.value.send_template),
  set: (recipient) => {
    actionInstructions.value = {
      ...actionInstructions.value,
      send_template: toSendTemplate(recipient),
    };
  },
});

// Validation rules
const rules = computed(() => ({
  transaction_type: {
    required: customValidators.required,
  },
}));

const v$ = useVuelidate(
  rules,
  computed(() => ({
    transaction_type: actionInstructions.value.transaction_type,
  })),
  {
    $autoDirty: true,
  }
);

const updateTransactionType = (type: PpaActionTransactionType) => {
  let updated = { ...actionInstructions.value };
  updated.transaction_type = type;

  // Clear templates when transaction type changes using utility function
  updated = initializeTransactionTemplates(updated, type);

  actionInstructions.value = updated;
  v$.value.transaction_type.$touch();
};

const updateCancelStatement = (statement: IndividualCancelStatement) => {
  const updated = { ...actionInstructions.value };
  updated.cancel_statement = statement;
  actionInstructions.value = updated;
};

// The recipient form is mounted only for a non-CNMC SEND; CNMC PPAs pick their destination in
// CnmcDestinationEditor, which the backend validates on save.
defineExpose({
  isValid: () =>
    !v$.value.$invalid && (sendRecipientForm.value?.isValid() ?? true),
  touchAndValidate: () => {
    v$.value.$touch();
    const recipientOk = sendRecipientForm.value?.touchAndValidate() ?? true;
    return !v$.value.$invalid && recipientOk;
  },
});
</script>

<style scoped lang="scss">
.counterparty-details-section__prompt {
  font-size: 12px;
  font-weight: 600;
  color: var(--phoenix-gray-700);
  margin-bottom: 8px;
  letter-spacing: 0.01em;
}

// Preserve the vertical rhythm the old inline callout had around the
// SegmentedControl + transaction details.
.counterparty-details-section__info {
  margin: 12px 0 18px;
}
</style>
